import React from "react";
import { useTranslation } from "react-i18next";

import {
  LANGUAGES,
  normalizeLanguage,
  saveLanguage,
} from "../../i18n/languages";

/**
 * FR | EN switch. Changes the whole website at once and remembers the choice in this
 * browser (localStorage "bolt.language", and a cookie that outlives signing out).
 * Styled like the theme switch.
 * variant: "compact" for the header, "row" for menus (with the word Language / Langue).
 */
const LanguageSwitch = ({ variant = "compact", className = "" }) => {
  const { t, i18n } = useTranslation("common");
  const current =
    normalizeLanguage(i18n.resolvedLanguage || i18n.language) || "en";

  const choose = (code) => {
    saveLanguage(code);
    if (code !== current) i18n.changeLanguage(code);
  };

  const group = (
    <div
      role="radiogroup"
      aria-label={t("language.label")}
      className={`inline-flex items-center gap-0.5 rounded-lg border border-ui-border bg-ui-surface-sunken p-0.5 ${
        variant === "row" ? "" : className
      }`}
    >
      {LANGUAGES.map(({ code, label, name }) => {
        const active = code === current;
        return (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={active}
            lang={code}
            title={name}
            aria-label={name}
            onClick={() => choose(code)}
            className={`rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
              active
                ? "bg-ui-surface text-brand shadow-ui-sm"
                : "text-ui-text-muted hover:text-brand"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  if (variant !== "row") return group;
  return (
    <div
      className={`flex items-center justify-between gap-2 px-3 py-2 text-sm text-ui-text-muted ${className}`}
    >
      <span>{t("language.label")}</span>
      {group}
    </div>
  );
};

export default LanguageSwitch;
